import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft } from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { BackToTop } from '../components/BackToTop';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const About = () => {
  const navigate = useNavigate();

  useEffect(() => {
    axios.post(`${API}/track-visit`, { path: '/about' }).catch(() => {});
  }, []);

  const stats = [
    { value: '3,000', label: 'Free welcome words', detail: 'Try the full service on your first document — no card required.' },
    { value: '16', label: 'Style presets', detail: 'From literary fiction to animation screenplays — your work, your voice.' },
    { value: '5', label: 'File formats', detail: 'TXT, DOCX, PDF, FDX, Fountain — import and export your way.' },
  ];

  return (
    <div className="w-full min-h-screen bg-background" data-testid="about-page">
      <header className="h-16 border-b border-border flex items-center justify-between px-6 md:px-10 bg-background">
        <div className="flex items-center gap-4">
          <Button
            onClick={() => navigate('/')}
            className="rounded-lg font-medium text-xs uppercase tracking-[0.2em] px-4 py-2 bg-transparent text-ivory border border-border hover:border-gold hover:text-gold"
            data-testid="back-btn"
          >
            <ArrowLeft size={14} weight="duotone" className="mr-2" />
            Back
          </Button>
          <h1 className="text-xl font-serif font-medium tracking-tight text-ivory">About</h1>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 md:px-12 py-16 md:py-24 animate-fade-up">
        <p className="text-[11px] font-medium uppercase tracking-[0.32em] text-ash mb-4">What is Manuscript Polish?</p>
        <h2 className="text-5xl md:text-6xl font-serif font-light tracking-tight text-ivory leading-[1.05] mb-10">
          A proofreader for <em className="text-gold not-italic font-normal">serious</em> writers.
        </h2>

        <div className="space-y-7 text-base text-bone font-sans leading-[1.85] max-w-2xl">
          <p>
            Manuscript Polish is an AI-assisted proofreading service built for novelists, screenwriters, and long-form writers who care about voice as much as correctness. It corrects grammar, spelling, punctuation, style, tone, and format-specific issues — without flattening the way you write.
          </p>
          <p>
            Powered by both OpenAI GPT-5.2 and Gemini 3 Pro, the tool understands the difference between literary fiction and a thriller, between a children's picture book and a documentary screenplay. Pick a genre preset, choose a priority level, and your manuscript receives the right kind of attention.
          </p>
          <p>
            Every correction is highlighted side-by-side so you can see exactly what changed before you accept it. Your downloaded file is always clean — no markup, no annotations, just the corrected version of your work.
          </p>
        </div>

        <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-5">
          {stats.map((s) => (
            <div key={s.label} className="panel-luxe p-7">
              <p className="text-4xl font-serif font-light text-gold mb-2">{s.value}</p>
              <p className="text-[11px] font-medium uppercase tracking-[0.28em] text-ash mb-3">{s.label}</p>
              <p className="text-sm text-bone font-sans leading-relaxed">{s.detail}</p>
            </div>
          ))}
        </div>

        {/* About the Creator */}
        <section className="mt-20 md:mt-24" data-testid="about-creator-section">
          <p className="text-[11px] font-medium uppercase tracking-[0.32em] text-ash mb-4">About the Creator</p>
          <h3 className="text-3xl md:text-4xl font-serif font-light tracking-tight text-ivory leading-[1.15] mb-2">
            Keith Williams
          </h3>
          <p className="text-sm font-medium uppercase tracking-[0.24em] text-gold mb-10">
            Novelist · Songwriter · Founder
          </p>

          <div className="space-y-6 text-base text-bone font-sans leading-[1.85] max-w-2xl">
            <p>
              Writing has been a major part of my life for decades. As both an author and songwriter, I understand the excitement, challenges, and dedication that go into creating original work.
            </p>
            <p>
              I have completed four novels, including <em className="text-ivory not-italic">Continuum</em>, a two-part sci-fi thriller. While science fiction is one of my passions, my writing experience spans multiple genres, including drama, comedy, romance, suspense, and thriller fiction.
            </p>
            <p>
              Alongside novel writing, I have spent more than forty years writing songs across a wide range of styles, including Hip Hop, Rap, Pop, R&amp;B, Soul, and Rock. Working across different creative disciplines has given me a deep appreciation for storytelling, structure, pacing, character development, and the importance of finding the right voice for your audience.
            </p>
            <p>
              Manuscript Polish was created because I saw a gap in the services available to writers. Too often, proofreading and manuscript reviews focus only on surface-level corrections. I wanted to create a service that looks deeper.
            </p>
            <p>
              By allowing writers to select the genre of their manuscript before the review begins, Manuscript Polish can provide more focused and relevant feedback, helping writers strengthen not only grammar and presentation, but also the elements that matter most within their chosen genre.
            </p>
            <p>
              The same philosophy inspired our <em className="text-ivory not-italic">Song Lyric Analysis</em> service. As a songwriter myself, I have always been interested in understanding how others interpret my work. Detailed lyric analysis can highlight strengths, themes, patterns, and opportunities for improvement while helping creators recognise what is already working well.
            </p>
            <p>
              My goal is simple: <span className="text-ivory">to help writers and songwriters present their work at its very best while preserving their unique voice and creative vision.</span>
            </p>
            <p className="pt-2 border-t border-border/60 font-serif italic text-ivory">
              Thank you for visiting Manuscript Polish. I look forward to being part of your creative journey.
            </p>

            {/* Author signature */}
            <div className="pt-4" data-testid="author-signature-block">
              <div
                className="signature text-gold text-5xl md:text-6xl leading-none mb-1 select-none"
                aria-label="Keith Williams signature"
                data-testid="author-signature"
              >
                Keith Williams
              </div>
              <p className="text-[10px] font-medium uppercase tracking-[0.32em] text-ash">
                Founder · Manuscript Polish
              </p>
            </div>
          </div>
        </section>

        <div className="mt-16 flex flex-wrap gap-3">
          <Button
            onClick={() => navigate('/how-to')}
            className="rounded-lg font-medium text-sm tracking-wide px-6 py-3 bg-transparent text-ivory border border-border hover:border-gold hover:text-gold"
            data-testid="how-to-link"
          >
            How to use →
          </Button>
          <Button
            onClick={() => navigate('/')}
            className="rounded-lg font-medium text-sm tracking-wide px-8 py-3 bg-gold text-charcoal hover:bg-gold-dark"
            data-testid="start-btn"
          >
            Start Proofreading
          </Button>
        </div>
      </main>
      <BackToTop />
    </div>
  );
};
